import Button from "@mui/material/Button";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import "./App.css";
import EventsDetails from "./DetailFunctions/EventsDetails";
import JobsDetails from "./DetailFunctions/JobsDetails";
import MedienDetails from "./DetailFunctions/MedienDetails";
import StoryDetails from "./DetailFunctions/StoryDetails";
import AnsprechpartnerOverview from "./OverviewFunctions/Ansprechpartner";
import EventOverview from "./OverviewFunctions/EventsOverview";
import FirmendatenOverview from "./OverviewFunctions/Firmendaten";
import JobOverview from "./OverviewFunctions/JobsOverview";
import MedienOverview from "./OverviewFunctions/MedienOverview";
import StoryOverview from "./OverviewFunctions/StoryOverview";
import useFetchConfig from "./fetchFunction/ConfigFetch";
import {
  Building2,
  Calendar,
  Folder,
  Briefcase,
  Users2,
  Newspaper
} from "lucide-react";


export const getCompanyAccessName = () => {
  if (typeof window !== "undefined" && window.exportedCompanyAccessName) {
    return window.exportedCompanyAccessName;
  }

  return "";
};

function App({ config, companyAccessName }) {
  const companyAccessNameRef = useRef("");
  useEffect(() => {
    companyAccessNameRef.current = companyAccessName;
    window.exportedCompanyAccessName = companyAccessName;
  }, [companyAccessName]);

  const [ansprechpartnerData] = useState([]);
  const [eventsData] = useState([]);
  const [firmendatenData] = useState([]);
  const [jobsData] = useState([]);
  const [medienData] = useState([]);
  const [storiesData] = useState([]);

  // Neu hinzugefügte Zustandsvariablen
  const [eventsDetailsData] = useState([]);
  const [jobsDetailsData] = useState([]);
  const [storiesDetailsData] = useState([]);
  const [medienDetailsData] = useState([]);

  const [showMore, setShowMore] = useState({});
  const [showDetailPage, setShowDetailPage] = useState(false);
  const {
    configSettings,
    content: fetchedContent,
    setContent: setFetchedContent,
  } = useFetchConfig(config, companyAccessName);

  useEffect(() => {
    if (fetchedContent) {
      setContent(fetchedContent);
    }
  }, [fetchedContent]);

  const getFontSize = (size) => {
    switch (size) {
      case "small":
        return "12px";
      case "medium":
        return "16px";
      case "large":
        return "20px";
      default:
        return "16px";
    }
  };

  const getNavbarPadding = (dimension) => {
    switch (dimension) {
      case "spacious":
        return "40px";
      case "default":
        return "20px";
      case "compressed":
        return "10px";
      default:
        return "5px";
    }
  };

  const styling = {
    linkColor: configSettings?.theme?.linkColor || "#007BFF",
    textColor: configSettings?.theme?.textColor || "#333333",
    border: configSettings?.theme?.border || "1px solid #dddddd",
    borderRadius: configSettings?.theme?.borderRadius || "4px",
    fontSize: getFontSize(configSettings?.theme?.fontSize),
    fontFamily: configSettings?.theme?.fontFamily || "sans-serif",
    navbarBackgroundColor:
      configSettings?.theme?.navbarBackgroundColor || "#f0f0f0",
    navbarButtonTextColor:
      configSettings?.theme?.navbarButtonTextColor || "#f0f0f0",
    navbarBackgroundHighlightColor:
      configSettings?.theme?.navbarBackgroundHighlightColor || "darkgrey",
    widgetContentBackgroundColor:
      configSettings?.theme?.widgetContentBackgroundColor || "#ffffff",
    navbarButtonBackgroundColor:
      configSettings?.theme?.navbarButtonBackgroundColor || "#f0f0f0",
    widgetButtonBackgroundColor:
      configSettings?.theme?.widgetButtonBackgroundColor || "#e0e0e0",
    navbarPadding: getNavbarPadding(configSettings?.theme?.navbarDimension),
    iconColor: configSettings?.theme?.iconColor || "#f0f0f0",
    activeButtonBackgroundColor:
      configSettings?.theme?.activeButtonBackgroundColor || "#007BFF",
    keywordBackgroundColor:
      configSettings?.theme?.keywordBackgroundColor || "#0077B5",
    categoryBackgroundColor:
      configSettings?.theme?.categoryBackgroundColor || "#0077B5",
    applyButtonBackgroundColor:
      configSettings?.theme?.applyButtonBackgroundColor || "#0077B5",
    applyButtonHoverBackgroundColor:
      configSettings?.theme?.applyButtonHoverBackgroundColor || "#0077B5",
    chapterEntry: {
      backgroundColor: "#ffffff",
      padding: "20px",
      border: "1px solid #ddd",
      borderRadius: "8px",
      boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
      transition: "box-shadow 0.3s ease, transform 0.3s ease",
    },
    chapterTitle: {
      fontSize: "1.2em",
      fontWeight: "bold",
      marginBottom: "10px",
    },
    chapterText: {
      fontSize: "1em",
      lineHeight: "1.6",
    },
    navbarButtonStyles: {
      backgroundColor: configSettings?.theme?.navbarButtonBackgroundColor,
      color: configSettings?.theme?.navbarButtonTextColor, // Corrected here
      padding: "10px 15px",
      borderRadius: configSettings?.theme?.borderRadius,
      boxShadow: "0px 2px 5px rgba(0,0,0,0.25)",
    },
    widgetButtonStyles: {
      backgroundColor: configSettings?.theme?.widgetButtonBackgroundColor,
      color: configSettings?.theme?.textColor,
      padding: "5px 10px",
      borderRadius: configSettings?.theme?.borderRadius || "4px",
      boxShadow: "0px 1px 3px rgba(0,0,0,0.15)",
      width: "auto",
      maxWidth: "200px",
      margin: "10px auto",
      display: "block",
    },
    activeButtonStyles: {
      backgroundColor: "darkgray",
      color:
        configSettings?.theme?.activeButtonTextColor ||
        configSettings?.theme?.textColor,
    },
  };

  const [content, setContent] = useState({
    navbarItems: [
      "Firmendaten",
      "Stories",
      "Events",
      "Jobs",
      "Ansprechpartner",
      "Medien",
    ],
    activeButton: "Firmendaten",
  });

  const [jsonData, setJsonData] = useState(null);

  const chapterData = {
    ansprechpartner: ansprechpartnerData,
    events: eventsData,
    firmendaten: firmendatenData,
    jobs: jobsData,
    medien: medienData,
    stories: storiesData,
    eventsDetails: eventsDetailsData,
    jobsDetails: jobsDetailsData,
    storiesDetails: storiesDetailsData,
    medienDetails: medienDetailsData,
  };

  useEffect(() => {
    if (content.activeButton) {
      setJsonData(chapterData[content.activeButton.toLowerCase()]);
    }
  }, [content.activeButton, chapterData]);

  const reloadChapter = (chapterName) => {
    setFetchedContent((prevContent) => ({
      ...prevContent,
      activeButton: chapterName,
      isReloading: true,
    }));
    setJsonData(null);
  };

  const handleButtonClick = (buttonText) => {
    if (content.activeButton === buttonText && showDetailPage) {
      setShowDetailPage(false);
    } else {
      setContent((prevContent) => ({
        ...prevContent,
        activeButton: buttonText,
      }));
      if (jsonData && jsonData.isReloading) {
        setFetchedContent((prevContent) => ({
          ...prevContent,
          isReloading: false,
        }));
        setShowMore({});
      } else {
        reloadChapter(buttonText);
      }
    }
  };

  const getChapterData = (chapter) => {
    return chapterData[chapter.toLowerCase()];
  };

  const renderAdditionalInfo = (info) => {
    return Object.entries(info).map(([key, value]) => {
      if (key === "headline") {
        return (
          <h3 key={key} style={{ fontWeight: "bold" }}>
            {value}
          </h3>
        );
      } else if (typeof value === "string" && key !== "image_url") {
        return (
          <p key={key}>
            {key === "text"
              ? value.substring(0, 200) +
                (value.length > 200 && !showMore[key] ? "..." : "")
              : value}
            {key === "text" && value.length > 200 && !showMore[key] && (
              <span>
                ...
                <button
                  onClick={() => setShowMore({ ...showMore, [key]: true })}
                >
                  Weiterlesen
                </button>
              </span>
            )}
          </p>
        );
      } else if (Array.isArray(value)) {
        return (
          <div key={key}>
            {value.map((item, index) => (
              <div key={index}>
                <a href={item.linkURL || item.categoryLink}>
                  {item.linkText || item.categoryName || item}
                </a>
              </div>
            ))}
          </div>
        );
      } else if (typeof value === "object") {
        return (
          <div key={key}>
            {Object.entries(value).map(([subKey, subValue]) => (
              <h2 key={subKey}>{subValue}</h2>
            ))}
          </div>
        );
      }

      return null;
    });
  };

  const [selectedMedienId, setSelectedMedienId] = useState(null);
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState(null);
  const [selectedStoryId, setSelectedStoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1); // Initialisierung des Zustands
  const [scrollPosition, setScrollPosition] = useState(0);

  useEffect(() => {
    const setMaxHeight = () => {
      const container = document.getElementById("unn-presscorner-widget");
      if (!container) return;

      const containerHeight = container.offsetHeight;
      const windowHeight = window.innerHeight;
      const availableHeight = windowHeight - container.offsetTop;

      const widgetContentContainer = container.querySelector(".widget-content");
      if (!widgetContentContainer) return;

      widgetContentContainer.style.maxHeight =
        Math.min(containerHeight, availableHeight) + "px";
    };

    setMaxHeight();
    window.addEventListener("resize", setMaxHeight);

    return () => {
      window.removeEventListener("resize", setMaxHeight);
    };
  }, [jsonData]);

  useEffect(() => {
    window.scrollTo(0, scrollPosition);
  }, [scrollPosition]);

  const renderChapterData = (chapterData) => {
    if (content.activeButton === "Jobs") {
      if (showDetailPage) {
        return (
          <JobsDetails
            companyAccessname={companyAccessName}
            jobsData={jobsDetailsData}
            setSelectedJobId={setSelectedJobId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            selectedJobId={selectedJobId}
          />
        );
      } else {
        return (
          <JobOverview
            companyAccessname={companyAccessName}
            jobDataList={jobsData}
            setCurrentPage={setCurrentPage}
            setScrollPosition={setScrollPosition}
            setSelectedJobId={setSelectedJobId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            currentPage={currentPage}
            formatTimestamp={formatTimestamp}
          />
        );
      }
    } else if (content.activeButton === "Events") {
      if (showDetailPage) {
        return (
          <EventsDetails
            companyAccessname={companyAccessName}
            eventsData={eventsDetailsData}
            setSelectedEventId={setSelectedEventId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            selectedEventId={selectedEventId}
          />
        );
      } else {
        return (
          <EventOverview
            companyAccessname={companyAccessName}
            eventDataList={eventsData}
            setCurrentPage={setCurrentPage}
            setScrollPosition={setScrollPosition}
            setSelectedEventId={setSelectedEventId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            currentPage={currentPage}
            formatTimestamp={formatTimestamp}
          />
        );
      }
    } else if (content.activeButton === "Stories") {
      if (showDetailPage) {
        return (
          <StoryDetails
            companyAccessname={companyAccessName}
            storiesData={storiesDetailsData}
            setSelectedStoryId={setSelectedStoryId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            selectedStoryId={selectedStoryId}
          />
        );
      } else {
        return (
          <StoryOverview
            companyAccessname={companyAccessName}
            storyDataList={storiesData}
            setCurrentPage={setCurrentPage}
            setScrollPosition={setScrollPosition}
            setSelectedStoryId={setSelectedStoryId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            currentPage={currentPage}
            formatTimestamp={formatTimestamp}
          />
        );
      }
    } else if (content.activeButton === "Firmendaten") {
      return (
        <FirmendatenOverview
          companyAccessname={companyAccessName}
          firmendaten={firmendatenData}
          styling={styling}
        />
      );
    } else if (content.activeButton === "Medien") {
      if (showDetailPage) {
        return (
          <MedienDetails
            companyAccessname={companyAccessName}
            medienData={medienDetailsData}
            setSelectedMedienId={setSelectedMedienId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
            selectedMedienId={selectedMedienId}
          />
        );
      } else {
        return (
          <MedienOverview
            companyAccessname={companyAccessName}
            medienData={medienData}
            setSelectedMedienId={setSelectedMedienId}
            setShowDetailPage={setShowDetailPage}
            styling={styling}
          />
        );
      }
    } else if (content.activeButton === "Ansprechpartner") {
      return (
        <AnsprechpartnerOverview
          companyAccessname={companyAccessName}
          styling={styling}
        />
      );
    } else if (Array.isArray(chapterData)) {
      return (
        <div>
          {chapterData.map((item, index) => (
            <div key={index} style={styling.chapterEntry}>
              {renderAdditionalInfo(item)}
            </div>
          ))}
        </div>
      );
    } else {
      return <span>{chapterData}</span>;
    }
  };

  const itemToIcon = {
    Firmendaten: Building2,
    Medien: Folder,
    Events: Calendar,
    Jobs: Briefcase,
    Stories: Newspaper,
    Ansprechpartner: Users2,
  };


  const formatTimestamp = (timestamp) => {
    const currentDate = new Date();
    const date = new Date(timestamp);
    const timeDifference = currentDate - date;
    const seconds = Math.floor(timeDifference / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    const months = Math.floor(days / 30);
    const years = Math.floor(months / 12);

    if (seconds < 60) {
      return "vor wenigen Sekunden";
    } else if (minutes === 1) {
      return "vor einer Minute";
    } else if (minutes < 60) {
      return `vor ${minutes} Minuten`;
    } else if (hours === 1) {
      return "vor einer Stunde";
    } else if (hours < 24) {
      return `vor ${hours} Stunden`;
    } else if (days === 1) {
      return "gestern";
    } else if (days < 7) {
      return `vor ${days} Tagen`;
    } else if (months === 1) {
      return "vor einem Monat";
    } else if (months < 12) {
      return `vor ${months} Monaten`;
    } else if (years === 1) {
      return "vor einem Jahr";
    } else {
      return `vor ${years} Jahren`;
    }
  };

  return (
    <div
      className="unnpcw-container"
      style={{
        backgroundColor: styling.backgroundColor,
        color: styling.textColor,
        fontSize: styling.fontSize,
        fontFamily: styling.fontFamily,
      }}
    >
      <div
        className="navbar"
        style={{
          backgroundColor: styling.navbarBackgroundColor,
          color: styling.navbarButtonTextColor,
          padding: styling.navbarPadding,
        }}
      >
        {content.navbarItems.map((item) => (
          <Button
          key={item}
          variant="contained"
          color="primary"
          style={{
            marginBottom: "5px",
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            fontSize: "16px",
            marginRight: "10px",
            backgroundColor:
              content.activeButton === item
                ? styling.navbarBackgroundHighlightColor
                : styling.navbarButtonBackgroundColor,
            color:
              content.activeButton === item
                ? styling.activeButtonStyles.color
                : styling.navbarButtonTextColor,
          }}
          onClick={() => handleButtonClick(item)}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            {itemToIcon[item] &&
              React.createElement(itemToIcon[item], {
                size: 20,
                color: styling.iconColor,
                style: { marginRight: "10px" },
              })}
            <span>{item}</span>
          </div>
        </Button>
        ))}
      </div>

      <div
        className="widget-content"
        style={{
          backgroundColor: styling.widgetContentBackgroundColor,
          color: styling.textColor,
          padding: "1rem",
          border: styling.border,
          borderRadius: styling.borderRadius,
        }}
      >
        {jsonData && content.activeButton && (
          <div>{renderChapterData(getChapterData(content.activeButton))}</div>
        )}
      </div>
    </div>
  );
}

App.propTypes = {
  config: PropTypes.shape({
    theme: PropTypes.shape({
      linkColor: PropTypes.string,
      textColor: PropTypes.string,
      border: PropTypes.string,
      borderRadius: PropTypes.string,
      fontSize: PropTypes.string,
      fontFamily: PropTypes.string,
      navbarBackgroundColor: PropTypes.string,
      widgetContentBackgroundColor: PropTypes.string,
      navbarButtonBackgroundColor: PropTypes.string,
      widgetButtonBackgroundColor: PropTypes.string,
      navbarButtonTextColor: PropTypes.string,
      navbarBackgroundHighlightColor: PropTypes.string,
      widgetButtonTextColor: PropTypes.string,
      navbarButtonPadding: PropTypes.string,
      widgetButtonPadding: PropTypes.string,
      navbarButtonBorderRadius: PropTypes.string,
      widgetButtonBorderRadius: PropTypes.string,
      navbarDimension: PropTypes.string,
      iconColor: PropTypes.string,
      activeButtonBackgroundColor: PropTypes.string,
      activeButtonTextColor: PropTypes.string,
      keywordBackgroundColor: PropTypes.string,
      categoryBackgroundColor: PropTypes.string,
      applyButtonBackgroundColor: PropTypes.string,
      applyButtonHoverBackgroundColor: PropTypes.string,
    }),
    navbarItems: PropTypes.arrayOf(PropTypes.object).isRequired,
    defaultActiveButton: PropTypes.string.isRequired,
    displayOptions: PropTypes.shape({
      showDetailPages: PropTypes.bool,
      enableAnimations: PropTypes.bool,
      formatTimestamps: PropTypes.bool,
    }).isRequired,
  }),
  companyAccessName: PropTypes.string.isRequired,
};

export default App;
